import { useState } from "react";
import { baseURL } from "../config/api";


type TireDetailCardProps = {
    primaryKey: null | number;
    data: any;
    selectedTires: any[];
    setSelectedTires: React.Dispatch<React.SetStateAction<any[]>>;
    selectedTireCount: number;
    status: string;
};

export const TireDetailCard = ({
    primaryKey,
    data,
    selectedTires,
    setSelectedTires,
    selectedTireCount,
    status
}: TireDetailCardProps) => {
    const [isUpdating, setIsUpdating] = useState(false);

    const disabled = data?.quantityOnHand < 1;

    const selectedItem = selectedTires.find(
        t => t.tireProductId === data?.tireProductId
    );

    const currentQty = selectedItem?.quantity || 0;

    const totalSelectedQty = selectedTires.reduce(
        (sum, item) => sum + item.quantity,
        0
    );

    const selectedQty =
        selectedTires.find(t => t.tireProductId === data?.tireProductId)
            ?.quantity || 0;

    const totalPrice = selectedQty * (data?.price || 0);

    const canIncrease =
        currentQty < data?.quantityOnHand &&
        totalSelectedQty < selectedTireCount;

    const handleAdd = () => {
        if (!primaryKey || status === "Scheduled" || status === "Queued" || status === "InProgress") {
            if (disabled || !canIncrease) return;
            withLoader(() => {
                setSelectedTires((prev: any) => [
                    ...prev,
                    {
                        reservationId: 0,
                        appointmentId: 0,
                        tireProductId: data.tireProductId,
                        locationId: data.locationId,
                        quantity: 1
                    }
                ])
            });
        }
    };

    const updateQuantity = (delta: number) => {
        if (!primaryKey || status === "Scheduled" || status === "Queued" || status === "InProgress") {
            withLoader(() => {
                setSelectedTires(prev =>
                    prev
                        .map(item => {
                            if (item.tireProductId !== data.tireProductId) return item;

                            const newQty = item.quantity + delta;

                            if (newQty < 1) return null;

                            if (
                                newQty > data.quantityOnHand ||
                                totalSelectedQty + delta > selectedTireCount
                            ) {
                                return item;
                            }

                            return { ...item, quantity: newQty };
                        })
                        .filter(Boolean) as any[]
                )
            });
        }
    };

    const withLoader = (action: () => void) => {
        if (!primaryKey || status === "Scheduled" || status === "Queued" || status === "InProgress") {
            if (isUpdating) return;

            setIsUpdating(true);
            action();

            // If later you call API, move this into `.finally()`
            setTimeout(() => setIsUpdating(false), 300);
        }
    };

    const Loader = () => (
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    );

    return (
        <div
            className={`max-w-2xl w-full rounded-md p-2 shadow-md bg-white flex gap-4
      ${disabled ? "opacity-50 pointer-events-none" : ""}`}
        >
            {/* Image */}
            <div className="w-[230px] bg-gray-200 rounded-2xl flex items-center justify-center">
                <img
                    className="w-full"
                    alt="tireImage"
                    src={
                        data?.productImage
                            ? `${baseURL}commonFile/Get?name=${data.productImage}`
                            : "./images/InventoryfallbackImg.png"
                    }
                    onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = "./images/InventoryfallbackImg.png";
                    }}
                />
            </div>

            {/* Details */}
            <div className="w-full">
                <div className="w-full border-b py-2">
                    <span className="text-lg font-semibold">{data?.brandName}</span><br />
                    <span className="text-lg font-semibold">{data?.modelName}</span>
                </div>

                <div className="py-2">
                    <div className="flex justify-between">
                        <div>
                            <span className="text-xs font-semibold">
                                In Stock: {data?.quantityOnHand}
                            </span><br />
                            <span className="text-xs font-semibold">
                                In House: {data?.inHouse ? "Yes" : "No"}
                            </span><br />
                            <span className="text-xs font-semibold">
                                Waiting Time In {data?.waitingTimeUnitValue}: {data?.waitingTimeValue}
                            </span>
                        </div>

                        <div className="text-end">
                            <span className="text-lg font-medium text-primary">
                                ${data?.price}
                            </span>
                            <br />
                            {
                                selectedQty > 0 && (
                                    <span className="text-xs text-gray-500">
                                        Set of {selectedQty}:&nbsp;
                                        <span className="font-semibold text-gray-900">
                                            ${totalPrice.toFixed(2)}
                                        </span>
                                    </span>
                                )
                            }

                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end py-3">
                        {!selectedItem ? (
                            <button
                                onClick={handleAdd}
                                disabled={disabled || isUpdating}
                                className={`${isUpdating ? "bg-white" : "bg-primary"} hover:bg-orange-600 text-white text-sm px-5 py-2 rounded-md font-medium transition
                                            flex items-center justify-center gap-2 disabled:opacity-50`}
                            >
                                {isUpdating ? <Loader /> : "Add"}
                            </button>
                        ) : (
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => updateQuantity(-1)}
                                    disabled={isUpdating}
                                    className="w-8 h-8 flex items-center justify-center bg-primary rounded-md text-lg disabled:opacity-50"
                                >
                                    −
                                </button>

                                <span className="min-w-[20px] text-center font-semibold">
                                    {isUpdating ? <Loader /> : currentQty}
                                </span>


                                <button
                                    onClick={() => updateQuantity(1)}
                                    disabled={!canIncrease || isUpdating}
                                    className="w-8 h-8 flex items-center justify-center bg-primary rounded-md text-lg disabled:opacity-50"
                                >
                                    +
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
